import React, { useContext } from 'react';
import { View, Pressable } from 'react-native';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import AuthContext from 'src/contexts/AuthContext';
import ConfirmDialog from 'src/elements/ConfirmDialog';
import Spinner from 'src/components/shared/Spinner';
import { Button, Notice } from 'src/elements';
import Input from 'src/elements/inputs';
import { Text } from 'src/styles';
import { dollars } from 'src/utils/stringHelpers';

import { CREATE_PRE_BID } from './queries';
import useBidSubmission from './useBidSubmission';
import {
  Container,
  TitleContainer,
  Title,
  Warning,
  SubmittingLabel,
} from './bidElements';
import { BidField, BidFieldRow } from './elements';

const TYPE_OPTIONS = [
  { value: 'Normal', label: 'Pre-Bid' },
  { value: 'Max', label: 'Max Bid' },
];

const Content = ({ asset, currentUser, refetch, onSuccess, setShow }) => {
  const { preBidData } = asset;

  let defaultPrice = preBidData?.preBidStartPrice;
  let minPrice = preBidData?.preBidStartPrice;

  if (preBidData?.highPreBidPrice) {
    defaultPrice = preBidData?.highPreBidPrice + preBidData?.preBidDefaultStep;
    minPrice = preBidData?.highPreBidPrice + preBidData?.preBidMinStep;

    if (preBidData?.preBidStartPrice > minPrice) {
      defaultPrice = preBidData?.preBidStartPrice;
      minPrice = preBidData?.preBidStartPrice;
    }
  }

  // A lot with no pre-bids yet can carry a start price of 0, which would both
  // advertise a $0 minimum and let the validator accept anything on-step.
  minPrice = Math.max(minPrice || 0, preBidData.preBidMinStep);
  defaultPrice = Math.max(defaultPrice || 0, minPrice);

  const prices = [];
  let currentPrice = minPrice;

  while (currentPrice <= defaultPrice) {
    prices.push(currentPrice);
    currentPrice = currentPrice + preBidData.preBidMinStep;
  }

  const priceOptions = prices.reverse().map(price => (
    { value: price.toString(), label: dollars(price) }
  ));

  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      saleAssetId: asset.activeSaleAsset.id,
      type: 'Normal',
      price: defaultPrice.toString(),
    }
  });

  const type = watch('type');

  const {
    requestSubmit, confirmation, confirm, cancel, submitting, error,
  } = useBidSubmission({
    mutation: CREATE_PRE_BID,
    payloadKey: 'createPreBid',
    // Must stay an arrow. `onSuccess: refetch` would hand Apollo the submitted
    // price as refetch variables, which is what tore this page down.
    // The bid box stays open so the refetched "Your current Max Bid" and the
    // success notice land in view, rather than collapsing back to the details.
    onSuccess: (variables) => {
      refetch();
      if (onSuccess) onSuccess(variables);
    },
  });

  const onSubmit = input => {
    const price = parseInt(input.price, 10);
    const label = 'Normal' === input.type ? 'Pre-Bid' : 'Max Bid';

    requestSubmit(
      { saleAssetId: input.saleAssetId, type: input.type, price },
      `Confirm ${label} of ${dollars(price)} for ${asset.name}`
    );
  };

  if (!currentUser?.buyer) {
    return (
      <Link to="/login">
        <Warning>
          Click here to log in or register in order to bid.
        </Warning>
      </Link>
    );
  } else if ('Review' === currentUser.buyer.status) {
    return (
      <Warning>
        Your account is currently being reviewed for approval to bid.
      </Warning>
    );
  } else if ('New' === currentUser.buyer.status) {
    return (
      <>
        <Warning>
          You must provide billing information before we can review
          your account.
        </Warning>
        <Link to="/account/billing">
          <Warning>
            Click Here to finish setting up your account.
          </Warning>
        </Link>
      </>
    );
  } else if (currentUser.buyer.isApproved) {
    return (
      <View>
        {error && <Notice type="error">{error}</Notice>}
        {preBidData.myPreBid && (
          <Text style={{ fontWeight: 600 }}>
            Your current{' '}
            {'Normal' === preBidData.myPreBid ? 'Pre-Bid' : 'Max Bid'}:{' '}
            {dollars(preBidData.myPreBid.price)}
          </Text>
        )}
        {preBidData.highPreBid && (
          <Text>
            Current high pre-bid: {dollars(preBidData.highPreBidPrice)}
          </Text>
        )}
        <BidFieldRow>
          <BidField>
            <Input.SearchablePicker
              label="Bid Type:"
              name="type"
              control={control}
              errors={errors}
              options={TYPE_OPTIONS}
              includeAny={false}
              placeholder="Select"
              rules={{ required: true }}
            />
          </BidField>
          <BidField>
            {'Normal' === type ? (
              <Input.SearchablePicker
                label="Price:"
                name="price"
                control={control}
                errors={errors}
                options={priceOptions}
                includeAny={false}
                placeholder="Select"
                rules={{ required: true }}
              />
            ) : (
              <Input.Currency
                label="Price:"
                name="price"
                control={control}
                errors={errors}
                rules={{
                  required: true,
                  validate: value => (
                    (
                      parseInt(value) >= minPrice &&
                      0 === value % preBidData.preBidMinStep
                    ) || (
                      `Must be at least ${dollars(minPrice)} in steps of ` +
                      `${dollars(preBidData.preBidMinStep)}`
                    )
                  )
                }}
              />
            )}
          </BidField>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={submitting}
            // Button is width:100% by default, which overflows a narrow row.
            // Weight matches NarrowButton, the other buttons on this page.
            style={{
              width: 175,
              maxWidth: '100%',
              flexShrink: 0,
              marginTop: 0,
              fontWeight: 800,
            }}
          >
            {submitting ? (
              <SubmittingLabel>
                <Spinner
                  $size="16px"
                  $track="rgba(255, 255, 255, 0.4)"
                  $accent="#fff"
                />
                Submitting…
              </SubmittingLabel>
            ) : 'Submit Bid'}
          </Button>
        </BidFieldRow>

        <ConfirmDialog
          open={!!confirmation}
          title="Confirm Your Bid"
          message={confirmation?.message}
          confirmLabel={
            'Max' === confirmation?.variables?.type
              ? 'Place Max Bid'
              : 'Place Pre-Bid'
          }
          onConfirm={confirm}
          onCancel={cancel}
        />
      </View>
    );
  }
};

const PreBid = ({ asset, setShow, refetch, onSuccess }) => {
  const { currentUser } = useContext(AuthContext);

  return (
    <Container>
      <TitleContainer>
        <Title>Enter Pre-Bid</Title>
        <Pressable onPress={() => setShow(false)}>
          <Title>X</Title>
        </Pressable>
      </TitleContainer>

      <Content
        asset={asset}
        currentUser={currentUser}
        refetch={refetch}
        onSuccess={onSuccess}
        setShow={setShow}
      />
    </Container>
  );
};

export default PreBid;
